import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import type { SemanticTone } from '@/shared/ui/core/types';

export type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
  tone?: SemanticTone;
  variant?: 'soft' | 'solid';
  label: string;
  icon?: ReactNode;
};

/**
 * Soft fills: pair with *-fg.
 * Solid fills: solid bg + text-white.
 */
const toneClass: Record<SemanticTone, string> = {
  neutral: 'bg-slate-100 text-slate-700 border-slate-300/80',
  primary: 'bg-primary-soft text-primary-fg border-primary/25',
  accent: 'bg-success-soft text-success-fg border-success/25',
  success: 'bg-success-soft text-success-fg border-success/25',
  warning: 'bg-[#FFC000]/15 text-[#8A5B00] border-[#FFC000]/40',
  danger: 'bg-[#C00000]/10 text-[#C00000] border-[#C00000]/25',
  info: 'bg-[#0070C0]/10 text-[#0070C0] border-[#0070C0]/25',
  hold: 'bg-[#C00000]/10 text-[#C00000] border-[#C00000]/25',
  draft: 'bg-[#E7E6E6]/60 text-[#262626] border-[#BFBFBF]',
};

const solidToneClass: Record<SemanticTone, string> = {
  neutral: 'bg-slate-700 text-white border-slate-600 shadow-xs',
  primary: 'bg-primary text-white border-emerald-700 shadow-xs',
  accent: 'bg-primary text-white border-emerald-700 shadow-xs',
  success: 'bg-primary text-white border-emerald-700 shadow-xs',
  warning: 'bg-[#FFC000] text-slate-900 border-[#e6ad00] shadow-xs',
  danger: 'bg-[#C00000] text-white border-[#a60000] shadow-xs',
  info: 'bg-[#0070C0] text-white border-[#005fa3] shadow-xs',
  hold: 'bg-[#C00000] text-white border-[#a60000] shadow-xs',
  draft: 'bg-[#E7E6E6] text-[#262626] border-[#BFBFBF] shadow-xs',
};

export function Badge({
  className,
  tone = 'neutral',
  variant = 'soft',
  label,
  icon,
  ...props
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 whitespace-nowrap items-center gap-1 rounded-pill border px-2.5 py-0.5 text-caption font-semibold',
        variant === 'solid' ? solidToneClass[tone] : toneClass[tone],
        className,
      )}
      {...props}
    >
      {icon ? <span className="inline-flex size-3.5 [&>svg]:size-3.5">{icon}</span> : null}
      {label}
    </span>
  );
}
